import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { C, S, BASE } from "../constants";
import { fmtUAH } from "../utils";
import Img from "./Img";

const RANGES = [
  { l: "30д",  days: 30 },
  { l: "90д",  days: 90 },
  { l: "180д", days: 180 },
  { l: "Все",  days: 0 },
];

function fmtDate(d) {
  if (!d) return "";
  const [, m, day] = d.split("-");
  const months = ["","січ","лют","бер","квіт","трав","черв","лип","серп","вер","жовт","лист","груд"];
  return `${parseInt(day)} ${months[parseInt(m)]}`;
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 8,
      padding: "8px 12px", fontSize: 12,
    }}>
      <div style={{ color: C.text3, marginBottom: 4 }}>{fmtDate(label)}</div>
      {payload.map((p, i) => p.value > 0 && (
        <div key={i} style={{ color: p.color, display: "flex", gap: 8, justifyContent: "space-between" }}>
          <span>{p.name}</span>
          <span style={{ fontWeight: 700 }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
}

function PosTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const pos = payload[0]?.value;
  return (
    <div style={{
      background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 8,
      padding: "8px 12px", fontSize: 12,
    }}>
      <div style={{ color: C.text3, marginBottom: 4 }}>{fmtDate(label)}</div>
      <div style={{ color: C.yellow }}>Позиція: <strong>{pos}</strong></div>
    </div>
  );
}

export default function ProductHistoryModal({ productId, productName, productImage, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(30);
  const [tab, setTab] = useState("sales"); // sales | position | analytics
  const [crossSell, setCrossSell] = useState(null);

  useEffect(() => {
    const tkn = localStorage.getItem("crm_token");
    setLoading(true);
    fetch(`${BASE}/api/products/${productId}/history`, {
      headers: { Authorization: "Bearer " + tkn }
    }).then(r => r.ok ? r.json() : null).then(d => {
      setData(d);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [productId]);

  useEffect(() => {
    if (tab !== "analytics") return;
    if (crossSell !== null) return;
    const tkn = localStorage.getItem("crm_token");
    fetch(`${BASE}/api/products/${productId}/cross-sell`, {
      headers: { Authorization: "Bearer " + tkn }
    }).then(r => r.ok ? r.json() : null).then(d => setCrossSell(d?.items || []));
  }, [tab, productId, crossSell]);

  // Filter by range
  const filteredDaily = (() => {
    if (!data?.daily) return [];
    if (range === 0) return data.daily;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - range);
    const cutoffStr = cutoff.toISOString().slice(0, 10);
    return data.daily.filter(d => d.date >= cutoffStr);
  })();

  const filteredPos = (() => {
    if (!data?.positions) return [];
    if (range === 0) return data.positions;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - range);
    const cutoffStr = cutoff.toISOString().slice(0, 10);
    return data.positions.filter(d => d.date >= cutoffStr);
  })();

  // Stats
  const totalQty = filteredDaily.reduce((s, d) => s + (d.qty || 0), 0);
  const totalBuyback = filteredDaily.reduce((s, d) => s + (d.buyback || 0), 0);
  const activeDays = filteredDaily.filter(d => d.qty > 0 || d.buyback > 0).length;
  const peakDay = filteredDaily.reduce((max, d) => (d.qty + d.buyback) > (max.qty + max.buyback) ? d : max, { qty: 0, buyback: 0, date: "" });
  const prod = data?.product || {};

  const modal = (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,.75)", backdropFilter: "blur(3px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          ...S.card,
          width: "100%", maxWidth: 900, maxHeight: "90vh",
          overflowY: "auto", padding: 0, display: "flex", flexDirection: "column",
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", gap: 14,
          padding: "16px 20px", borderBottom: `1px solid ${C.border}`,
        }}>
          <Img src={productImage || prod.image_url} size={48} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text1, marginBottom: 2,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {productName || prod.name}
            </div>
            <div style={{ display: "flex", gap: 12, fontSize: 11, color: C.text3 }}>
              {prod.article && <span>Арт: {prod.article}</span>}
              {prod.catalog_price > 0 && <span>Ціна: {fmtUAH(prod.catalog_price)}</span>}
              {prod.catalog_promo_price > 0 && (
                <span style={{ color: C.pink }}>Промо: {fmtUAH(prod.catalog_promo_price)}</span>
              )}
              {prod.is_ff && <span style={{ color: C.purple, fontWeight: 700 }}>FF</span>}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", color: C.text3, fontSize: 20,
            cursor: "pointer", padding: "0 4px", lineHeight: 1,
          }}>✕</button>
        </div>

        {/* Controls */}
        <div style={{
          display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
          padding: "12px 20px", borderBottom: `1px solid ${C.border}`,
        }}>
          {/* Range tabs */}
          <div style={{ display: "flex", gap: 4 }}>
            {RANGES.map(r => (
              <button key={r.days} onClick={() => setRange(r.days)} style={{
                padding: "4px 12px", borderRadius: 6, fontSize: 12, cursor: "pointer",
                background: range === r.days ? C.blue : C.bg2,
                color: range === r.days ? "#fff" : C.text3,
                border: `1px solid ${range === r.days ? C.blue : C.border}`,
              }}>{r.l}</button>
            ))}
          </div>
          {/* Chart type tabs */}
          <div style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
            {[["sales", "Продажі"], ["position", "Позиція"], ["analytics", "Аналітика"]].map(([t, l]) => (
              <button key={t} onClick={() => setTab(t)} style={{
                padding: "4px 12px", borderRadius: 6, fontSize: 12, cursor: "pointer",
                background: tab === t ? C.bg3 : "transparent",
                color: tab === t ? C.text1 : C.text3,
                border: `1px solid ${tab === t ? C.border : "transparent"}`,
              }}>{l}</button>
            ))}
          </div>
        </div>

        {/* Stats row */}
        {!loading && data && (
          <div style={{
            display: "flex", gap: 0, borderBottom: `1px solid ${C.border}`,
          }}>
            {[
              ["Продажі", totalQty + " шт.", C.blue],
              ["Викупи", totalBuyback > 0 ? totalBuyback + " шт." : "—", C.yellow],
              ["Активних днів", activeDays, C.green],
              ["Пік", peakDay.date ? `${fmtDate(peakDay.date)}: ${peakDay.qty + peakDay.buyback} шт.` : "—", C.text2],
            ].map(([label, val, color]) => (
              <div key={label} style={{
                flex: 1, padding: "10px 16px", borderRight: `1px solid ${C.border}`,
                textAlign: "center",
              }}>
                <div style={{ fontSize: 10, color: C.text4, marginBottom: 3 }}>{label}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color }}>{val}</div>
              </div>
            ))}
          </div>
        )}

        {/* Chart */}
        <div style={{ padding: "16px 20px 20px", flex: 1 }}>
          {loading && (
            <div style={{ textAlign: "center", color: C.text3, padding: 60 }}>Завантаження...</div>
          )}

          {!loading && tab === "sales" && (
            filteredDaily.length === 0
              ? <div style={{ textAlign: "center", color: C.text4, padding: 40 }}>Немає даних за цей період</div>
              : (
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={filteredDaily} barGap={0} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tick={{ fill: C.text4, fontSize: 9 }}
                      tickFormatter={d => fmtDate(d)}
                      interval={Math.max(0, Math.floor(filteredDaily.length / 12) - 1)} />
                    <YAxis tick={{ fill: C.text4, fontSize: 9 }} allowDecimals={false} width={28} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11, color: C.text3 }} />
                    <Bar dataKey="qty" name="Продажі" fill={C.blue} opacity={0.8}
                      radius={[2, 2, 0, 0]} maxBarSize={20} />
                    <Bar dataKey="buyback" name="Викупи" fill={C.yellow} opacity={0.8}
                      radius={[2, 2, 0, 0]} maxBarSize={20} />
                  </ComposedChart>
                </ResponsiveContainer>
              )
          )}

          {!loading && tab === "position" && (
            filteredPos.length === 0
              ? <div style={{ textAlign: "center", color: C.text4, padding: 40 }}>Немає даних про позиції. Запусти парсер позицій.</div>
              : (
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={filteredPos} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tick={{ fill: C.text4, fontSize: 9 }}
                      tickFormatter={d => fmtDate(d)}
                      interval={Math.max(0, Math.floor(filteredPos.length / 10) - 1)} />
                    <YAxis tick={{ fill: C.text4, fontSize: 9 }} reversed width={32}
                      label={{ value: "Позиція", angle: -90, position: "insideLeft", fill: C.text4, fontSize: 9 }} />
                    <Tooltip content={<PosTooltip />} />
                    <Line dataKey="pos" name="Позиція" stroke={C.yellow} strokeWidth={2}
                      dot={{ r: 3, fill: C.yellow }} activeDot={{ r: 5 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              )
          )}

          {!loading && tab === "analytics" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {/* Promo Effect */}
              {data?.promo_effect && (data.promo_effect.promo || data.promo_effect.regular) && (() => {
                const promo = data.promo_effect.promo;
                const reg = data.promo_effect.regular;
                const lift = promo && reg && reg.avg_daily > 0
                  ? Math.round((promo.avg_daily / reg.avg_daily - 1) * 100) : null;
                return (
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.text1, marginBottom: 10 }}>Ефект промо</div>
                    <div style={{ display: "flex", gap: 12 }}>
                      {reg && (
                        <div style={{ flex: 1, background: C.bg2, borderRadius: 8, padding: "10px 14px" }}>
                          <div style={{ fontSize: 10, color: C.text4, marginBottom: 4 }}>Звичайна ціна</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: C.text1 }}>{reg.avg_daily}<span style={{ fontSize: 11, color: C.text3, marginLeft: 3 }}>шт/день</span></div>
                          <div style={{ fontSize: 10, color: C.text4, marginTop: 2 }}>{reg.qty} шт за {reg.days} днів</div>
                        </div>
                      )}
                      {promo && (
                        <div style={{ flex: 1, background: C.bg2, borderRadius: 8, padding: "10px 14px" }}>
                          <div style={{ fontSize: 10, color: C.text4, marginBottom: 4 }}>Промо ціна</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: C.green }}>{promo.avg_daily}<span style={{ fontSize: 11, color: C.text3, marginLeft: 3 }}>шт/день</span></div>
                          <div style={{ fontSize: 10, color: C.text4, marginTop: 2 }}>{promo.qty} шт за {promo.days} днів</div>
                        </div>
                      )}
                      {lift !== null && (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "0 8px" }}>
                          <div style={{ fontSize: 20, fontWeight: 900, color: lift > 0 ? C.green : C.pink }}>
                            {lift > 0 ? "+" : ""}{lift}%
                          </div>
                        </div>
                      )}
                    </div>
                    {!promo && <div style={{ color: C.text4, fontSize: 11 }}>Товар не продавався в промо</div>}
                    {!reg && <div style={{ color: C.text4, fontSize: 11 }}>Товар продавався тільки в промо</div>}
                  </div>
                );
              })()}

              {/* OOS Position Impact */}
              {data?.oos_impact && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.text1, marginBottom: 10 }}>Вплив відсутності на позицію</div>
                  <div style={{ background: `${C.pink}18`, border: `1px solid ${C.pink}40`, borderRadius: 8, padding: "10px 14px" }}>
                    <div style={{ fontSize: 11, color: C.text3, marginBottom: 6 }}>
                      Нема в наявності з {new Date(data.oos_impact.oos_since).toLocaleDateString("uk-UA")}
                    </div>
                    <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                      {data.oos_impact.position_before !== null && (
                        <div><div style={{ fontSize: 10, color: C.text4 }}>До OOS</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: C.green }}>#{data.oos_impact.position_before}</div></div>
                      )}
                      <div style={{ fontSize: 18, color: C.text4 }}>→</div>
                      {data.oos_impact.position_current !== null
                        ? <div><div style={{ fontSize: 10, color: C.text4 }}>Зараз</div>
                            <div style={{ fontSize: 18, fontWeight: 800, color: C.pink }}>#{data.oos_impact.position_current}</div></div>
                        : <div style={{ color: C.text4, fontSize: 11 }}>Позиція не відстежується</div>}
                    </div>
                  </div>
                </div>
              )}

              {/* Cross-sell */}
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text1, marginBottom: 10 }}>Купують разом</div>
                {crossSell === null
                  ? <div style={{ color: C.text4, fontSize: 11 }}>Завантаження...</div>
                  : crossSell.length === 0
                    ? <div style={{ color: C.text4, fontSize: 11 }}>Цей товар не купували з іншими в одному замовленні</div>
                    : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {crossSell.map((item, i) => (
                          <div key={item.rozetka_id} style={{ display: "flex", alignItems: "center", gap: 10,
                            background: C.bg2, borderRadius: 8, padding: "8px 12px" }}>
                            <span style={{ fontSize: 11, color: C.text4, width: 18, flexShrink: 0 }}>#{i + 1}</span>
                            {item.image_url && <img src={item.image_url} alt="" style={{ width: 32, height: 32, objectFit: "contain", borderRadius: 4, flexShrink: 0 }} />}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <a href={`https://rozetka.com.ua/ua/${item.rozetka_id}/p${item.rozetka_id}/`} target="_blank" rel="noreferrer"
                                style={{ fontSize: 12, color: C.text1, textDecoration: "none", fontWeight: 600,
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
                                {item.name}
                              </a>
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 700, color: C.blue, flexShrink: 0 }}>{item.co_count}×</span>
                          </div>
                        ))}
                      </div>
                    )
                }
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
